// Lightweight Web Audio sound effects — no external files needed.
// Each helper builds short synthesized tones on the fly.
//
// Sounds can be silenced via localStorage `cashback_sounds_muted=1`.

let _ctx = null;
function getCtx() {
  if (typeof window === "undefined") return null;
  if (_ctx) return _ctx;
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    _ctx = new AC();
    return _ctx;
  } catch {
    return null;
  }
}

function isMuted() {
  try { return localStorage.getItem("cashback_sounds_muted") === "1"; }
  catch { return false; }
}

function ensureRunning(ctx) {
  if (ctx.state === "suspended") {
    // Browsers require a user gesture; this is called from a click handler so it'll work.
    ctx.resume().catch(() => {});
  }
}

// Short click: tiny tick using a quick exponential decay.
export function playClick() {
  if (isMuted()) return;
  const ctx = getCtx();
  if (!ctx) return;
  ensureRunning(ctx);
  try {
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "triangle";
    osc.frequency.setValueAtTime(880, now);
    osc.frequency.exponentialRampToValueAtTime(440, now + 0.05);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.12, now + 0.005);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.07);
    osc.connect(gain).connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.08);
  } catch {}
}

// Spinning wheel — repeated "tick" pattern that slows down AND climbs in pitch
// near the end, creating the classic slot-machine suspense reveal.
// Returns an object with a `stop()` method.
export function playSpinTicks(durationMs = 4000) {
  if (isMuted()) return { stop() {} };
  const ctx = getCtx();
  if (!ctx) return { stop() {} };
  ensureRunning(ctx);
  const startedAt = performance.now();
  let stopped = false;
  const totalMs = durationMs;

  function tick() {
    if (stopped) return;
    const elapsed = performance.now() - startedAt;
    if (elapsed >= totalMs) return;
    const t = Math.min(1, elapsed / totalMs);
    try {
      const now = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      // Phase-based sound design:
      //   t < 0.65 → low square wave ticks (mechanical/wood)
      //   t ≥ 0.65 → climbing pitched dings (slot-machine reveal)
      if (t < 0.65) {
        osc.type = "square";
        osc.frequency.setValueAtTime(1400, now);
        gain.gain.setValueAtTime(0.0001, now);
        gain.gain.exponentialRampToValueAtTime(0.08, now + 0.003);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.04);
        osc.connect(gain).connect(ctx.destination);
        osc.start(now);
        osc.stop(now + 0.05);
      } else {
        // Climbing-pitch "ding" — pitch rises with t. Reveal phase.
        const phase = (t - 0.65) / 0.35; // 0..1 over last 35%
        const f = 1200 + phase * 900; // 1200 → 2100 Hz
        osc.type = "triangle";
        osc.frequency.setValueAtTime(f, now);
        gain.gain.setValueAtTime(0.0001, now);
        gain.gain.exponentialRampToValueAtTime(0.18, now + 0.006);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.12);
        osc.connect(gain).connect(ctx.destination);
        osc.start(now);
        osc.stop(now + 0.13);
      }
    } catch {}
    // Ease-out spacing: ticks start at ~70ms apart and lengthen to ~300ms.
    const next = 70 + Math.pow(t, 1.6) * 230;
    setTimeout(tick, next);
  }
  tick();
  return { stop() { stopped = true; } };
}

// Celebration chord on win — quick rising arpeggio + a hold.
export function playWin() {
  if (isMuted()) return;
  const ctx = getCtx();
  if (!ctx) return;
  ensureRunning(ctx);
  try {
    const now = ctx.currentTime;
    // C major arpeggio: C5 E5 G5 C6
    const notes = [523.25, 659.25, 783.99, 1046.5];
    notes.forEach((freq, i) => {
      const t = now + i * 0.08;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "triangle";
      osc.frequency.setValueAtTime(freq, t);
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.18, t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.35);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.4);
    });
    // Final shimmery hold
    const hold = ctx.createOscillator();
    const holdGain = ctx.createGain();
    hold.type = "sine";
    hold.frequency.setValueAtTime(1046.5, now + 0.32);
    holdGain.gain.setValueAtTime(0.0001, now + 0.32);
    holdGain.gain.exponentialRampToValueAtTime(0.12, now + 0.35);
    holdGain.gain.exponentialRampToValueAtTime(0.0001, now + 1.0);
    hold.connect(holdGain).connect(ctx.destination);
    hold.start(now + 0.32);
    hold.stop(now + 1.05);
  } catch {}
}

// Soft "no win" tone — gentle descending pair.
export function playLose() {
  if (isMuted()) return;
  const ctx = getCtx();
  if (!ctx) return;
  ensureRunning(ctx);
  try {
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(440, now);
    osc.frequency.exponentialRampToValueAtTime(220, now + 0.3);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.10, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.35);
    osc.connect(gain).connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.4);
  } catch {}
}

// Global click handler — attached once. Plays a tick when a button-like
// element is clicked. Skips inputs/textareas/links by design.
let _globalAttached = false;
export function installGlobalClickSound() {
  if (typeof document === "undefined" || _globalAttached) return;
  _globalAttached = true;
  document.addEventListener(
    "click",
    (e) => {
      const t = e.target;
      if (!t) return;
      // Walk up to find a button or [role=button] within 4 levels.
      let el = t;
      for (let i = 0; i < 4 && el; i++) {
        if (
          el.tagName === "BUTTON" ||
          el.getAttribute?.("role") === "button" ||
          el.getAttribute?.("data-sound") === "click"
        ) {
          // Honour an opt-out attribute
          if (el.getAttribute?.("data-sound") === "off") return;
          playClick();
          return;
        }
        el = el.parentElement;
      }
    },
    true
  );
}
